import React, { useCallback, useEffect, useRef, useState } from 'react';
import { styled } from '@linaria/react';
import Text from '@components/Text';
import { useTheme, ITheme } from 'src/theme';
import Icon from '@components/Icon';


const Container = styled.div<StyledProps>`
  box-sizing: border-box;
  border-bottom: ${props => props.underline ? `3px solid ${props.theme?.colors.grey[700]}` : ''};
  margin-bottom: 10px;
`


const AccordionHeader = styled.div<StyledProps>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
`

const IconContainer = styled.div<StyledProps>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${props => props.isOpen ? props.theme?.colors.primary : props.theme?.colors.white};
  margin-right: 20px;
  display: flex;
  align-items: center;
  border:${props => props.isOpen ? `1px solid ${props.theme?.colors.primary}` : `1px solid ${props.theme?.colors.grey[700]}`};
  justify-content: center;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'unset'};
  animation-duration: .35s;
  animation-iteration-count: 1;
  animation-name: m-showhide;
  transition: background-color .25s linear,transform .25s linear;
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: ${props => props.theme?.colors.primary};
      border-color: ${props => props.theme?.colors.primary};
    }
  }
  img {
    margin-bottom: -2px;
  }
}
`;

const TitleContainer = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const AccordionContent = styled.div<StyledProps>`
  height: ${props => props.isOpen ? props.height + '' : '0px' };
  overflow: hidden;
	transition: height .18s linear;
  padding-top: 12px;
  margin-bottom: ${props => props.isOpen ? '14px' : 0};
`;

const HiddenContainer = styled.div<StyledProps>`
  height: ${props => props.isOpen ? 'auto' : '0px'};
  display: block;
  transition: height .18s linear;
`; 

interface StyledProps {
  theme: ITheme;
  isOpen?: boolean;
  height?: string;
  underline?: boolean;
}

export interface AccordionProps {
  className?: string | undefined;
  id?: string | undefined;
  theme?: ITheme;
  title: string;
  rightElement: JSX.Element | undefined,
  children: React.ReactNode;
  underline?: boolean;
  isExpanded: boolean;
}

function Accordion({ children, title, rightElement, className, id, underline, isExpanded }: AccordionProps) {
  const openIcon = 'chevron-down-white', closeIcon = 'chevron-standard-down-grey';
  const theme: ITheme = useTheme();
  const [isOpen, setIsOpen] = useState<boolean>(isExpanded);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<string>('auto')
  const [icon, setIcon] = useState<string>(isExpanded ? openIcon : closeIcon);
  const toggling = useCallback(() => {
    setIsOpen(pre => {
      const next = !pre;
      setIcon(next ? openIcon: closeIcon);
      return next;
    });
  }, []);

  const hoverEvent = () => {
    setIcon(openIcon);
  }

  const onBlurEvent = () => {
    setIcon(isOpen ? openIcon : closeIcon);
  }
  

  useEffect(() => {
    setTimeout(() => {
      if(contentRef.current?.clientHeight) {
        setHeight(contentRef.current?.clientHeight + 'px');
      }
    }, 500)
    const handleResize = () => {
			setHeight(contentRef.current?.clientHeight + 'px');
		}
		window.addEventListener('resize', handleResize)
  }, [isOpen, height, contentRef])

  return <Container theme={theme} className={className} id={id} underline={underline}>
    <AccordionHeader theme={theme} className="aviva-accordion-header" >
      <TitleContainer theme={theme} onClick={toggling} onMouseEnter={hoverEvent} onMouseLeave={onBlurEvent}>
        <IconContainer className="aviva-accordion-icon" theme={theme} isOpen={isOpen}>
          <Icon name={icon} size="sm" />
        </IconContainer>
        <Text className="aviva-accordion-title" fontWeight="bold" color={theme.colors.grey[400]}>{title}</Text>
      </TitleContainer>
      {isOpen && rightElement}
    </AccordionHeader>
    <AccordionContent theme={theme} isOpen={isOpen} height={height} className="aviva-accordion-body">
      <HiddenContainer theme={theme} isOpen={isOpen} ref={contentRef}>
        {children}
      </HiddenContainer>
    </AccordionContent>
  </Container>
};

Accordion.defaultProps = {
  rightElement: undefined,
  isExpanded: false
}

export default Accordion;
